import React from 'react';
import { Upload } from 'antd';
import { RcFile } from 'antd/es/upload';
import { UploadFile } from 'antd/lib/upload/interface';
import { InboxOutlined } from '@ant-design/icons';
import { colorsPalette } from '../../styles/colors-palette';
import styled, { css } from 'styled-components';

const { Dragger } = Upload;

type UploadStatus = 'error' | 'success' | 'done' | 'uploading' | 'removed';

interface UploadedFileData {
  name?: string;
  percent?: number;
  status?: UploadStatus;
  fileObj?: File;
}

interface FileUploadDraggerCustomStyles {
  background?: Color;
  borderColor?: Color;
  borderRadius?: CSSSize;
  paddingBlock?: CSSSize;
}

interface IFileUploadDragger {
  onRemove?: (fileData: UploadedFileData) => void;
  onSelect?: (file: File) => void;
  fileList?: UploadedFileData[];
  children?: React.ReactNode;
  showUploadedFile?: boolean;
  errorText?: string;
  className?: string;
  accept?: string;
  acceptTypes?: string;
  disabled?: boolean;
  multiple?: boolean;
  maxCount?: CountNumber;
  customStyles?: FileUploadDraggerCustomStyles;
}

const FileUploadDragger = function ({
  onSelect,
  onRemove,
  fileList,
  children,
  showUploadedFile,
  className,
  accept,
  acceptTypes,
  disabled,
  multiple,
  maxCount,
  customStyles,
}: IFileUploadDragger) {
  const onAntBeforeUpload = (file: RcFile) => {
    onSelect && onSelect(file);

    return false;
  };

  const antFileList: UploadFile[] | undefined = fileList?.map(file => {
    return {
      uid: '',
      ...file,
    } as UploadFile;
  });

  return (
    <StyledDragger
      className={className}
      accept={accept ?? acceptTypes}
      disabled={disabled}
      multiple={multiple}
      maxCount={maxCount}
      showUploadList={showUploadedFile}
      fileList={antFileList}
      beforeUpload={onAntBeforeUpload}
      onRemove={onRemove}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      customstyles={customStyles}
    >
      <BodyWrapper>
        <InboxOutlinedIcon width={35} height={35} />
        <HeaderText>Нажмите или перетащите файл</HeaderText>
        {children}
      </BodyWrapper>
    </StyledDragger>
  );
};

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeaderText = styled.div`
  font-size: 16px;
  color: ${colorsPalette.gray600};
  margin-bottom: 10px;
`;

const InboxOutlinedIcon = styled(InboxOutlined)`
  color: ${colorsPalette.dustyBlue500};
  font-size: 35px;
`;

const StyledDragger = styled(Dragger).withConfig({
  shouldForwardProp: prop => (prop as string) !== 'customstyles',
})<{ customstyles?: FileUploadDraggerCustomStyles }>`
  &.ant-upload-wrapper .ant-upload-drag:not(.ant-upload-disabled):hover {
    border-color: ${colorsPalette.dustyBlue500};
  }

  &.ant-upload-wrapper .ant-upload-drag.ant-upload-disabled {
    opacity: 0.5;

    &,
    .ant-upload-btn {
      cursor: not-allowed;
    }
  }

  ${({ customstyles }) =>
    customstyles &&
    css`
      &.ant-upload-wrapper .ant-upload-drag {
        background: ${customstyles.background};
        border-style: dashed;
        border-color: ${customstyles.borderColor};
        border-radius: ${customstyles.borderRadius};
        padding-block: ${customstyles.paddingBlock};
      }
    `}
` as typeof Dragger;

export { FileUploadDragger };
